import { useApi } from '@/api';
import { FancyText, ProgressBar } from '@/components';
import type { ProgressBarProps } from '@/components/ProgressBar';
import { PROTOCOL_COLORS } from '@/constants';
import { useChartTooltip } from '@/hooks';
import {
  ASSET_SYMBOLS_PALETTE,
  Box,
  Card,
  CurrencyValue,
  DisplaySwitch,
  DoughnutChart,
  Flex,
  formatPercentage,
  hexToRgb,
  rgbaToHex,
  TableSkeleton,
  Text
} from '@blockanalitica/ui';
import _ from 'lodash';
import { useMemo, useState } from 'react';

type RevenueResponse = {
  address: string;
  network: string;
  protocol: string | null;
  profit_estimated_usd: string;
  profit_total_usd: string;
  symbol: string;
  asset_symbol: string;
};

interface RevenueData extends Omit<ProgressBarProps, 'color'> {
  percent: number;
  label: string;
  rgba: { r: number; g: number; b: number; a: number };
}

function dataByProtocol(
  data: RevenueResponse[],
  field: keyof RevenueResponse
): RevenueData[] {
  const grouped = _.groupBy(data, 'protocol');
  const totalUsd = _.sumBy(data, (obj) => Number(obj[field]));

  const results = _.map(grouped, (items, protocol) => {
    const groupUsd = _.sumBy(items, (obj) => Number(obj[field]));

    const breakdown = items.map((item) => ({
      text: (
        <Flex gap="3xs">
          {item.asset_symbol}

          <Box as="span" color="muted">
            ({_.capitalize(item.network)})
          </Box>
        </Flex>
      ),
      percent: Number(item[field as keyof RevenueResponse]) / groupUsd,
      additionalValue: (
        <CurrencyValue
          value={item[field as keyof RevenueResponse]}
          options={{ currency: 'USD' }}
        />
      )
    }));

    return {
      label: protocol,
      rgba: hexToRgb(PROTOCOL_COLORS[protocol] || '#ffffff'),
      text: _.capitalize(protocol),
      additionalValue: (
        <CurrencyValue value={groupUsd} options={{ currency: 'USD' }} />
      ),
      percent: groupUsd / totalUsd,
      breakdown: _.orderBy(breakdown, ['percent'], ['desc'])
    };
  });
  return results;
}

function dataByAsset(
  data: RevenueResponse[],
  field: keyof RevenueResponse
): RevenueData[] {
  const grouped = _.groupBy(data, 'asset_symbol');
  const totalUsd = _.sumBy(data, (obj) => Number(obj[field]));

  const results = _.map(grouped, (items, asset_symbol) => {
    const groupUsd = _.sumBy(items, (obj) => Number(obj[field]));

    const breakdown = items.map((item) => ({
      text: (
        <Flex gap="3xs">
          {item.protocol ? _.capitalize(item.protocol) : null}

          <Box as="span" color="muted">
            ({_.capitalize(item.network)})
          </Box>
        </Flex>
      ),
      percent: Number(item[field as keyof RevenueResponse]) / groupUsd,
      additionalValue: (
        <CurrencyValue
          value={item[field as keyof RevenueResponse]}
          options={{ currency: 'USD' }}
        />
      )
    }));

    return {
      label: asset_symbol,
      rgba: hexToRgb(ASSET_SYMBOLS_PALETTE[asset_symbol] || '#ffffff'),
      text: asset_symbol,
      additionalValue: (
        <CurrencyValue value={groupUsd} options={{ currency: 'USD' }} />
      ),
      percent: groupUsd / totalUsd,
      breakdown: _.orderBy(breakdown, ['percent'], ['desc'])
    };
  });
  return results;
}

export default function RevenueBreakdown({
  network,
  showChart = true,
  field = 'profit_estimated_usd'
}: {
  network: string;
  showChart?: boolean;
  field?: string;
}) {
  const [activeDisplayOption, setActiveDisplayOption] = useState('protocol');

  const [data, error] = useApi<RevenueResponse[]>({
    url: '/sparkstar/sll/revenue/',
    queryParams: {}
  });

  const displayOptions = useMemo(
    () => [
      { key: 'protocol', value: 'By Protocol' },
      { key: 'asset', value: 'By Asset' }
    ],
    []
  );

  const orderedResult = useMemo(() => {
    if (!data) {
      return;
    }

    const filteredData = _.filter(data, function (o) {
      return o.network === network;
    });
    let results;
    switch (activeDisplayOption) {
      case 'protocol':
        results = dataByProtocol(filteredData, field as keyof RevenueResponse);
        break;
      case 'asset':
        results = dataByAsset(filteredData, field as keyof RevenueResponse);
        break;
    }

    return _.orderBy(results, ['percent'], ['desc']);
  }, [data, activeDisplayOption, field, network]);

  const customTooltip = useChartTooltip(
    orderedResult?.map(({ text, percent, additionalValue }) => ({
      text,
      percent,
      additionalValue
    })),
    'text',
    'percent',
    'additionalValue'
  );

  const chartData = useMemo(() => {
    if (!orderedResult) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];

    orderedResult.forEach((row) => {
      const { r, g, b } = row.rgba;
      labels.push(_.capitalize(row.label));
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(row.percent);
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [orderedResult]);

  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: customTooltip
          }
        }
      }
    }),
    [customTooltip]
  );

  if (error) {
    throw error;
  }

  if (!chartData) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <>
      <Flex justifyContent="space-between">
        <Flex alignItems="center" gap="3xs">
          <FancyText fontSize="-1">Breakdown</FancyText>
          <Text color="muted" fontSize="-1">
            {`(${_.capitalize(activeDisplayOption)})`}
          </Text>
        </Flex>
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
      </Flex>
      <Flex gap="s">
        {showChart && (
          <Card flexDirection="column" gap="m" backgroundColor="foreground">
            {/* 
              DoughnutChart needs to be wrapped in Flex (technically div) in order
              for the custom tooltip to attach to the correct parent
            */}
            <Flex>
              <DoughnutChart
                config={customConfig}
                data={chartData}
                valueFormatter={formatPercentage}
              />
            </Flex>
          </Card>
        )}
        <Flex flexDirection="column" flex="1" gap="s">
          {orderedResult?.map(({ label, rgba, ...row }) => (
            <ProgressBar key={label} color={rgbaToHex(rgba)} {...row} />
          ))}
        </Flex>
      </Flex>
    </>
  );
}
